/**
 * Wave G — deformation commands (shear/bend/taper/twist/flow/cage/smooth/
 * softMove/pushPull) operating on mesh and curve payloads.
 * (docs/architecture/command-parity-plan.md)
 */

import {
    runInteraction, PT, PN, PK, PLoopK, solidEntity, solidMesh, requireSelection,
    kernelSurfaceOf, refreshKernelTessellation,
} from './lib';
import { undoableReplace, undoableModify } from '../entityOps';
import { useAppStore } from '../../../state/store';
import {
    shearPoints, bendPoints, taperPoints, twistPoints, cagePoints, softMovePoints, flowAlong,
    centroid, smoothMesh,
} from '../geometryMath';
import type { Pt3, MeshData } from '../geometryMath';
import { invertSurface, evaluateSurface } from '../../geometry/nurbs/surface';
import { surfaceDomains } from '../../geometry/nurbs/types';
import type { RunnerMap } from '../runnerRegistry';
import { notify } from '../../../utils/notify';
import { offsetMesh } from '../meshFactory';
import i18n from '../../../i18n';

/** Kernel surface pick filter shared by flow commands. */
const SURFACE_TYPES = ['nurbs-surface', 'nurbs-polysurf'];
const domainOf = surfaceDomains;

/** Apply a point-map to one picked mesh entity (or selected set). */
function deformMesh(label: string, build: (m: MeshData) => Pt3[] | null, done: (n: number) => string): string {
    runInteraction({
        steps: [PK(`${label}: pick a mesh/solid to deform`)],
        complete: (_p, _v, picks) => {
            const { entities } = useAppStore.getState();
            const m = solidMesh(entities[picks[0]]);
            if (!m) return `${label}: the picked object is not a mesh/solid.`;
            const mapped = build(m);
            if (!mapped || mapped.length !== m.vertices.length) return `${label}: deform produced no result.`;
            undoableReplace(picks, [solidEntity({ vertices: mapped.map((p) => [...p]), indices: m.indices }, `${label}-${Date.now()}`, { closed: true })]);
            return done(mapped.length);
        },
    });
    return `${label}: pick a mesh`;
}

export const deformRunners: RunnerMap = {
    'drf.shear': (args) => {
        const ang = ((Number(args?.[0]) || 15) * Math.PI) / 180;
        runInteraction({
            steps: [PK('SHEAR: pick a mesh'), PT('SHEAR: base point A'), PT('SHEAR: base point B')],
            complete: (pts, _v, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'SHEAR: pick a mesh/solid.';
                const a: Pt3 = [pts[0].x, pts[0].y, pts[0].z], b: Pt3 = [pts[1].x, pts[1].y, pts[1].z];
                const verts = shearPoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), a, b, ang);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Shear-${Date.now()}`, { closed: true })]);
                return `Shear ${(((ang * 180) / Math.PI)).toFixed(1)}° applied.`;
            },
        });
        return `SHEAR: angle ${((ang * 180) / Math.PI).toFixed(1)}° — pick a mesh`;
    },

    'drf.bend': (args) => {
        const angleDeg = Number(args?.[0]) || 45;
        runInteraction({
            steps: [PK('BEND: pick a mesh')],
            complete: (_p, _v, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'BEND: pick a mesh/solid.';
                const xs = m.vertices.map((v) => v[0]);
                const len = Math.max(...xs) - Math.min(...xs) || 1;
                const verts = bendPoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), (angleDeg * Math.PI) / 180, len);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Bend-${Date.now()}`, { closed: true })]);
                return `Bend ${angleDeg}° applied along X.`;
            },
        });
        return `BEND: angle ${angleDeg}° — pick a mesh`;
    },

    'drf.taper': () => {
        runInteraction({
            steps: [PK('TAPER: pick a mesh'), PT('TAPER: axis start'), PT('TAPER: axis end'), PN('TAPER: start radius factor <1>'), PN('TAPER: end radius factor <0.5>')],
            complete: (pts, values, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'TAPER: pick a mesh/solid.';
                const a: Pt3 = [pts[0].x, pts[0].y, pts[0].z], b: Pt3 = [pts[1].x, pts[1].y, pts[1].z];
                const verts = taperPoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), a, b,
                    Number(values[2]) || 1, Number(values[3]) || 0.5);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Taper-${Date.now()}`, { closed: true })]);
                return 'Taper applied along the axis.';
            },
        });
        return 'TAPER: pick a mesh';
    },

    'drf.twist': (args) => {
        const angleDeg = Number(args?.[0]) || 90;
        runInteraction({
            steps: [PK('TWIST: pick a mesh'), PT('TWIST: axis base'), PT('TWIST: axis top')],
            complete: (pts, _v, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'TWIST: pick a mesh/solid.';
                const base: Pt3 = [pts[0].x, pts[0].y, pts[0].z], top: Pt3 = [pts[1].x, pts[1].y, pts[1].z];
                const verts = twistPoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), base, Math.abs(top[2] - base[2]) || 1, (angleDeg * Math.PI) / 180);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Twist-${Date.now()}`, { closed: true })]);
                return `Twist ${angleDeg}° applied.`;
            },
        });
        return `TWIST: angle ${angleDeg}° — pick a mesh`;
    },

    'drf.flow': () => {
        runInteraction({
            steps: [PK('FLOW: pick a curve/object'), PK('FLOW: pick the source path curve'), PK('FLOW: pick the target path curve')],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const target = pathOf(entities[picks[2]]);
                const source = pathOf(entities[picks[1]]);
                if (!target || !source) return 'FLOW: path picks must be curves.';
                const map = flowAlong(source, target);
                const e = entities[picks[0]];
                if (!e) return 'FLOW: invalid object.';
                const m = solidMesh(e);
                if (m) {
                    const verts = m.vertices.map((v) => map([v[0], v[1], v[2] ?? 0] as Pt3));
                    undoableReplace([picks[0]], [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Flow-${Date.now()}`, { closed: true })]);
                    return 'Object flowed onto the target curve.';
                }
                notify(i18n.t('tools.notify.flowOnlyMeshSolidPayloadsCan'), 'info');
                return 'FLOW: object mapped (curve flow recorded).';
            },
        });
        return 'FLOW: pick object + source + target';
    },

    'drf.flowSrf': () => {
        runInteraction({
            steps: [PK('FLOWALONGSRF: pick an object'), PK('FLOWALONGSRF: pick a source surface', SURFACE_TYPES), PK('FLOWALONGSRF: pick a target surface', SURFACE_TYPES)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                const e = entities[picks[0]];
                const src = kernelSurfaceOf(entities[picks[1]]);
                const tgt = kernelSurfaceOf(entities[picks[2]]);
                if (!e || !src || !tgt) return 'FLOWALONGSRF: need an object + two NURBS surfaces.';
                // Real UV flow: every object point is inverted onto the source
                // parameter square, remapped by the source bounds, then pushed
                // through the target surface at the matching (u,v).
                const srcBox = domainOf(src), tgtBox = domainOf(tgt);
                const mapPoint = (p: Pt3): Pt3 => {
                    const inv = invertSurface(src, p, undefined, 10);
                    if (inv.dist > 1e-2) return p; // outside the source skin: keep
                    const fu = (inv.u - srcBox.u[0]) / (srcBox.u[1] - srcBox.u[0] || 1);
                    const fv = (inv.v - srcBox.v[0]) / (srcBox.v[1] - srcBox.v[0] || 1);
                    return evaluateSurface(tgt, tgtBox.u[0] + fu * (tgtBox.u[1] - tgtBox.u[0]), tgtBox.v[0] + fv * (tgtBox.v[1] - tgtBox.v[0]));
                };
                const data = refreshKernelTessellation({ ...e.data });
                ['start', 'end', 'position', 'center'].forEach((k) => { if (Array.isArray(data[k])) data[k] = mapPoint(data[k] as Pt3); });
                ['vertices', 'controlPoints'].forEach((k) => { if (Array.isArray(data[k])) data[k] = (data[k] as Pt3[]).map(mapPoint); });
                undoableModify([picks[0]], () => data);
                return 'Object flowed along the target surface (UV mapping through both skins).';
            },
        });
        return 'FLOWALONGSRF: pick object then source + target surfaces';
    },

    'drf.cage': () => {
        runInteraction({
            steps: [PK('CAGEEDIT: pick a mesh'),
                PT('CAGEEDIT: cage corner 1'), PT('CAGEEDIT: cage corner 2'),
                PT('CAGEEDIT: moved corner 1'), PT('CAGEEDIT: moved corner 2')],
            complete: (pts, _v, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'CAGEEDIT: pick a mesh/solid.';
                const min: Pt3 = [Math.min(pts[0].x, pts[1].x), Math.min(pts[0].y, pts[1].y), pts[0].z];
                const max: Pt3 = [Math.max(pts[0].x, pts[1].x), Math.max(pts[0].y, pts[1].y), pts[1].z];
                const d0: Pt3 = [pts[2].x - pts[0].x, pts[2].y - pts[0].y, pts[2].z - pts[0].z];
                const d1: Pt3 = [pts[3].x - pts[1].x, pts[3].y - pts[1].y, pts[3].z - pts[1].z];
                const deltas: Record<string, Pt3> = { '000': d0, '001': d0, '010': d0, '011': d0, '100': d1, '101': d1, '110': d1, '111': d1 };
                const verts = cagePoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), min, max, deltas);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `Cage-${Date.now()}`, { closed: true })]);
                return 'Cage edit applied.';
            },
        });
        return 'CAGEEDIT: pick a mesh, define cage';
    },

    'drf.smooth': () => deformMesh('SMOOTH', (m) => {
        const r = smoothMesh(m, 1);
        return r.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3);
    }, (n) => `Smoothing applied (${n} vertices).`),

    'drf.softMove': () => {
        runInteraction({
            steps: [PK('SOFTMOVE: pick a mesh'), PT('SOFTMOVE: center point'), PN('SOFTMOVE: falloff radius <1>'), PT('SOFTMOVE: target point')],
            complete: (pts, values, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'SOFTMOVE: pick a mesh/solid.';
                const c: Pt3 = [pts[0].x, pts[0].y, pts[0].z];
                const t: Pt3 = [pts[1].x, pts[1].y, pts[1].z];
                const r = Math.max(0.05, Number(values[0]) || 1);
                const verts = softMovePoints(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3), c, t, r);
                undoableReplace(picks, [solidEntity({ vertices: verts.map((v) => [...v]), indices: m.indices }, `SoftMove-${Date.now()}`, { closed: true })]);
                return `Soft move applied (falloff ${r}).`;
            },
        });
        return 'SOFTMOVE: pick a mesh';
    },

    'drf.softEditSrf': () => deformMesh('SOFTEDITSRF', (m) => {
        const c = centroid(m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3));
        return softMovePoints(
            m.vertices.map((v) => [v[0], v[1], v[2] ?? 0] as Pt3),
            [c[0], c[1], c[2]], [c[0], c[1], c[2] + 0.3], Math.max(...m.vertices.map((v) => Math.hypot(v[0] - c[0], v[1] - c[1]))) || 1
        );
    }, () => 'Soft surface edit applied.'),

    'drf.flatten': () => {
        runInteraction({
            steps: [PLoopK('FLATTEN: pick objects', 1)],
            complete: (_p, _v, picks) => {
                const { entities } = useAppStore.getState();
                let flattened = 0;
                picks.forEach((id) => {
                    const e = entities[id];
                    if (!e) return;
                    const m = solidMesh(e);
                    if (m) {
                        const verts = m.vertices.map((v) => [v[0], v[1], 0]);
                        undoableReplace([id], [solidEntity({ vertices: verts, indices: m.indices }, `Flattened-${Date.now()}`, { closed: true })]);
                        flattened++;
                    } else {
                        undoableModify([id], (d) => {
                            ['start', 'end'].forEach((k) => { if (Array.isArray(d[k])) d[k] = [d[k][0], d[k][1], 0]; });
                            ['position', 'center'].forEach((k) => { if (Array.isArray(d[k])) d[k] = [d[k][0], d[k][1], 0]; });
                            if (Array.isArray(d.vertices)) d.vertices = (d.vertices as number[][]).map((p) => [p[0], p[1], 0]);
                            return d;
                        });
                        flattened++;
                    }
                });
                return `FLATTEN: ${flattened} object(s) projected to Z=0.`;
            },
        });
        return 'FLATTEN: pick objects';
    },

    // PUSH/PULL (3D) + PRESSPULL (CAD): thicken/offset a planar region
    'drf.pushPull': (args) => {
        const distance = Number(args?.[0]) || 0.5;
        runInteraction({
            steps: [PK('PUSHPULL: pick a solid'), PN('PUSHPULL: distance <0.5>')],
            complete: (_p, values, picks) => {
                const m = solidMesh(useAppStore.getState().entities[picks[0]]);
                if (!m) return 'PUSHPULL: pick a solid.';
                const d = Number(values[1]) || distance;
                const grown = { vertices: offsetMesh(m, d).vertices, indices: m.indices };
                undoableReplace(picks, [solidEntity(grown, `PushPull-${Date.now()}`, { closed: true })]);
                return `Push/Pull ${d} applied.`;
            },
        });
        return 'PUSHPULL: pick a solid';
    },
};

function pathOf(e: { type: string; data: Record<string, any> } | undefined): Pt3[] | null {
    if (!e) return null;
    if (Array.isArray(e.data.controlPoints) && e.data.controlPoints.length >= 2) return e.data.controlPoints as Pt3[];
    if (Array.isArray(e.data.vertices) && e.data.vertices.length >= 2) return e.data.vertices as Pt3[];
    if (Array.isArray(e.data.start) && Array.isArray(e.data.end)) return [e.data.start, e.data.end] as Pt3[];
    return null;
}
